import React, { useCallback } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import styles from './SocialLogin.module.css';

interface SocialLoginProps {
  onGoogleSuccess?: (response: any) => void;
  onGoogleError?: (error: any) => void;
}

/**
 * SocialLogin
 * - 仅保留 Google 登录（Facebook / Apple 已停用：未集成）
 * - Google 按钮严格遵循 Google Brand Guidelines：
 *   - 白底 / 1px #dadce0 边框 / 4px 圆角 / 官方阴影
 *   - 彩色 G logo（inline SVG，避免外链）
 *   - 文案 "Sign in with Google"（官方推荐）
 * - 使用 useGoogleLogin hook 触发 OAuth popup，按钮 UI 完全自定义
 */

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '804347936135-h7t7lvcfobidto0vma40jfq30f6jgp08.apps.googleusercontent.com';

// Google 官方 4 色 G logo（inline SVG，遵守 Google Brand Guidelines）
const GoogleLogo = () => (
  <svg
    className={styles.googleLogo}
    viewBox="0 0 48 48"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

const SocialLoginInner: React.FC<SocialLoginProps> = ({
  onGoogleSuccess,
  onGoogleError
}) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isGoogleConfigured =
    !!GOOGLE_CLIENT_ID && !GOOGLE_CLIENT_ID.includes('your-google-client-id');

  const handleSuccess = useCallback(
    (tokenResponse: any) => {
      if (onGoogleSuccess) onGoogleSuccess(tokenResponse);
    },
    [onGoogleSuccess]
  );

  const handleError = useCallback(
    (error?: any) => {
      if (onGoogleError) {
        onGoogleError(
          error?.message ||
            'Google 登录失败：请检查 REACT_APP_GOOGLE_CLIENT_ID 配置'
        );
      }
    },
    [onGoogleError]
  );

  // useGoogleLogin 在 OAuth 完成后调用 onSuccess，返回 access_token
  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: handleError,
    flow: 'implicit',
    scope: 'openid email profile'
  });

  const handleClick = () => {
    if (!isGoogleConfigured) {
      alert('请先在 .env 中配置 REACT_APP_GOOGLE_CLIENT_ID');
      return;
    }
    triggerGoogleLogin();
  };

  return (
    <div className={styles.socialLogin}>
      <div className={styles.separator}>
        <span className={styles.separatorText}>Or</span>
      </div>

      {isDevelopment && !isGoogleConfigured && (
        <div className={styles.devHint}>
          <strong>⚠️ Google 登录未配置</strong>
          <br />
          请在 <code>.env</code> 中设置有效的{' '}
          <code>REACT_APP_GOOGLE_CLIENT_ID</code>。
        </div>
      )}

      <div className={styles.socialButtons}>
        <div className={styles.socialButton}>
          <button
            type="button"
            className={styles.googleButton}
            onClick={handleClick}
            aria-label="Sign in with Google"
          >
            <GoogleLogo />
            <span className={styles.googleText}>Sign in with Google</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const SocialLogin: React.FC<SocialLoginProps> = (props) => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <SocialLoginInner {...props} />
  </GoogleOAuthProvider>
);

export default SocialLogin;
